import React, { useState, useEffect } from 'react';
import { IMAGES, Icons } from '../constants';
import { CONTENT } from '../content';
import { motion, AnimatePresence } from 'motion/react';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Profile } from '../types';

const OverMij: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const docRef = doc(db, 'profile', 'liza');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProfile(docSnap.data() as Profile);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    fetchProfile();
  }, []);

  const handleOpenCV = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('openCVModal'));
  };

  const imageSrc = profile?.image || IMAGES.about;
  const p1Text = profile?.p1 || CONTENT.overMij.p1;
  const p2Text = profile?.p2 || CONTENT.overMij.p2;
  const pMoreText = profile?.pMore || CONTENT.overMij.pMore;
  const titleText = profile?.title || CONTENT.overMij.title;

  return (
    <div className="py-32 bg-liza-bg relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="flex flex-col lg:flex-row gap-20 items-center">
          <div className="w-full lg:w-1/2 relative">
            <div className="absolute -inset-10 bg-accent-light opacity-40 speels-blob z-0 transform -rotate-12 scale-110"></div>
            <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl transition-transform duration-700 hover:scale-[1.02]">
              <img 
                src={imageSrc} 
                alt={CONTENT.cv.name} 
                className="w-full h-full object-cover"
                width="600"
                height="800"
                loading="lazy"
              />
            </div>
            <div className="absolute bottom-10 -right-5 bg-primary text-white p-8 rounded-full shadow-2xl z-20 hidden md:block animate-bounce">
              <span className="text-[10px] uppercase tracking-widest font-bold text-center block" dangerouslySetInnerHTML={{ __html: CONTENT.overMij.badgeSide }}></span>
            </div>
          </div>
          
          <div className="w-full lg:w-1/2">
            <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-6 block">{CONTENT.overMij.badge}</span>
            <h2 className="text-4xl md:text-5xl font-serif text-primary mb-10" dangerouslySetInnerHTML={{ __html: titleText }}></h2>
            <div className="prose prose-lg text-gray-600 space-y-6 font-light leading-relaxed max-w-xl">
              <p className="whitespace-pre-line">
                {p1Text}
              </p>
              <p className="whitespace-pre-line">
                {p2Text}
              </p>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="pt-4 pb-2 whitespace-pre-line">
                      {pMoreText}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-accent font-serif italic text-sm hover:text-accent-dark transition-colors group"
              >
                <span>{isExpanded ? 'Minder lezen' : 'Lees meer over mijn verhaal'}</span>
                <Icons.ChevronRight className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
              </button>
              
              <div className="space-y-10">
                <div className="grid grid-cols-2 gap-8 pt-8 border-t border-accent-light">
                  <div>
                    <h3 className="text-primary font-bold uppercase tracking-[0.2em] text-[10px] mb-4">{CONTENT.overMij.specializationsTitle}</h3>
                    <ul className="text-xs space-y-2 text-gray-500 font-medium">
                      {CONTENT.overMij.specializations.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-primary font-bold uppercase tracking-[0.2em] text-[10px] mb-4">{CONTENT.overMij.styleTitle}</h3>
                    <ul className="text-xs space-y-2 text-gray-500 font-medium">
                      {CONTENT.overMij.styles.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    onClick={handleOpenCV}
                    className="group flex items-center gap-4 text-primary hover:text-primary-dark transition-all duration-300"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                      <Icons.FileText />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold border-b border-primary/20 group-hover:border-primary transition-all pb-1">
                      {CONTENT.overMij.cvButton}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverMij;